import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./PermissionsManager.css";

const permTypes = ["", "X", "RW", "OR", "X*", "RW*", "OR*"];

export default function PermissionsManager({ onClose }) {
  const [data, setData] = useState(null);
  const [changed, setChanged] = useState(false);

  // User overrides
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOverride, setUserOverride] = useState(null);

  const searchRef = useRef();

  useEffect(() => {
    axios.get("/backend/api/permissions").then(r => setData(r.data));
    axios.get("/backend/api/users").then(r => setUsers(r.data));
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setUserResults([]);
      return;
    }
    const q = search.toLowerCase();
    setUserResults(
      users.filter(
        u =>
          (`${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q)) &&
          u.email
      )
    );
  }, [search, users]);

  function handleSelectUser(u) {
    setSelectedUser(u);
    setSearch(`${u.firstName} ${u.lastName}`);
    if (data?.userOverrides?.[u.email]) {
      setUserOverride({ ...data.userOverrides[u.email] });
    } else {
      setUserOverride({ role: "", permissions: {} });
    }
    setUserResults([]);
  }

  function handleUserRoleChange(role) {
    setUserOverride(uo => ({
      ...uo,
      role
    }));
    setChanged(true);
  }

  function handleUserPermChange(perm, value) {
    setUserOverride(uo => ({
      ...uo,
      permissions: {
        ...uo.permissions,
        [perm]: value
      }
    }));
    setChanged(true);
  }

  async function handleSave() {
    let newData = { ...data };
    if (selectedUser && userOverride) {
      if (!newData.userOverrides) newData.userOverrides = {};
      newData.userOverrides[selectedUser.email] = { ...userOverride };
    }
    setData(newData);
    await axios.post("/backend/api/permissions", newData);
    setChanged(false);
    alert("Permissions saved!");
  }

  // Close (fa fallback su reload pagina se onClose non è passato)
  const handleClose = () => {
    if (typeof onClose === "function") onClose();
    else window.location.href = "/";
  };

  if (!data) return <div className="messaging-main-panel"><div className="messaging-header-row"><h2>Loading…</h2></div></div>;
  const perms = Object.keys(data.roles[Object.keys(data.roles)[0]]);
  const allRoles = Object.keys(data.roles);

  return (
    <div className="messaging-main-panel perm-panel">
      <div className="messaging-header-row">
        <img src="/images/logo.png" alt="QPSC Logo" className="qpsc-logo" />
        <span className="messaging-title">Permissions Management</span>
      </div>

      {/* Role Matrix */}
      <div className="recipient-rows">
        <div className="recipient-row">
          <span className="recipient-label">Role Matrix:</span>
          <span style={{color:"#fbc02d", fontWeight:400}}>Set global permissions for each role.</span>
        </div>
      </div>
      <div className="messaging-table-wrapper">
        <table className="perm-table">
          <thead>
            <tr>
              <th>Function</th>
              {allRoles.map(role => (
                <th key={role}>{role}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perms.map(perm => (
              <tr key={perm}>
                <td>{perm.replace(/_/g, " ")}</td>
                {allRoles.map(role => (
                  <td key={role}>
                    <select
                      value={data.roles[role][perm] || ""}
                      onChange={e => {
                        setData(d => ({
                          ...d,
                          roles: {
                            ...d.roles,
                            [role]: { ...d.roles[role], [perm]: e.target.value }
                          }
                        }));
                        setChanged(true);
                      }}
                      className="messaging-select"
                    >
                      {permTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Single User Overrides */}
      <div className="recipient-rows" style={{marginTop:36}}>
        <div className="recipient-row">
          <span className="recipient-label" style={{minWidth:168}}>Single User Overrides:</span>
          <div style={{ position: "relative", width: "334px", marginBottom: userResults.length > 0 ? `${userResults.length * 49 + 20}px` : "26px" }}>
            <div className="messaging-user-autocomplete">
              <input
                ref={searchRef}
                type="text"
                placeholder="Search user by name or email…"
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setSelectedUser(null);
                  setUserOverride(null);
                }}
                className="messaging-typearea"
                autoComplete="off"
              />
              {userResults.length > 0 && (
                <div className="messaging-autocomplete-dropdown">
                  {userResults.slice(0,7).map(u =>
                    <div
                      key={u.email}
                      className="messaging-autocomplete-option"
                      onClick={() => handleSelectUser(u)}
                    >
                      <b>{u.firstName} {u.lastName}</b> <span className="messaging-autocomplete-email">({u.email})</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {selectedUser && userOverride && (
        <div className="messaging-override-box">
          <div className="recipient-row" style={{marginTop:8, marginBottom:8}}>
            <span className="recipient-label" style={{minWidth:120}}>Role override:</span>
            <select
              value={userOverride.role || ""}
              onChange={e => handleUserRoleChange(e.target.value)}
              className="messaging-select messaging-override-role"
            >
              <option value="">(Default)</option>
              {allRoles.map(role =>
                <option key={role} value={role}>{role}</option>
              )}
            </select>
            <span style={{fontSize:"0.98em", color:"#aaa", marginLeft:16}}>(leave blank for default role)</span>
          </div>
          <div className="messaging-table-wrapper">
            <table className="perm-table">
              <thead>
                <tr>
                  <th>Function</th>
                  <th>Override</th>
                </tr>
              </thead>
              <tbody>
                {perms.map(perm => (
                  <tr key={perm}>
                    <td>{perm.replace(/_/g, " ")}</td>
                    <td>
                      <select
                        value={userOverride.permissions[perm] || ""}
                        onChange={e => handleUserPermChange(perm, e.target.value)}
                        className="messaging-select"
                      >
                        <option value="">(default)</option>
                        {permTypes.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="messaging-footer-row">
        <button className="messaging-btn messaging-btn-close" onClick={handleClose}>Close</button>
        <button className="messaging-btn messaging-btn-save" onClick={handleSave} disabled={!changed}>Save Permissions</button>
      </div>
    </div>
  );
}